import type {
	FieldPath,
	FieldValues,
	UseControllerProps,
} from 'react-hook-form'

import React from 'react'
import { CircleHelpIcon, FileIcon, XIcon } from 'lucide-react'
import prettyBytes from 'pretty-bytes'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
	Dropzone,
	DropzoneInput,
	DropzoneUploadIcon,
	DropzoneZone,
} from '@/shared/components/ui/dropzone'
import {
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

import {
	Attachment,
	AttachmentAction,
	AttachmentActions,
	AttachmentContent,
	AttachmentDescription,
	AttachmentMedia,
	AttachmentTitle,
} from '../ui/attachment'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '../ui/empty'

export default function RHFFileDropArea<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	name,
	label,
	hint,
	defaultValue,
	shouldUnregister,
	multiple,
	description,
	...other
}: UseControllerProps<TFieldValues, TName> &
	Omit<React.ComponentProps<typeof Dropzone>, 'onDropAccepted' | 'children'> & {
		label?: string
		hint?: string
		required?: boolean
		description?: string
		className?: string
	}) {
	const { t } = useTranslation()

	const { control } = useFormContext()

	return (
		<FormField
			name={name}
			defaultValue={defaultValue}
			shouldUnregister={shouldUnregister}
			control={control}
			render={({ field }) => (
				<FormItem>
					{!!label && (
						<FormLabel>
							{label}
							{hint && (
								<Tooltip>
									<TooltipTrigger asChild>
										<CircleHelpIcon className='size-3 text-muted-foreground' />
									</TooltipTrigger>
									<TooltipContent side='top'>{hint}</TooltipContent>
								</Tooltip>
							)}
						</FormLabel>
					)}

					<Dropzone
						multiple={multiple}
						accept={{
							'text/csv': ['.csv'],
							'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
								['.xls', '.xlsx'],
							'application/vnd.ms-excel': ['.xls', '.xlsx'],
						}}
						{...other}
						onDropAccepted={(data) => field.onChange(data)}
					>
						<div className='grid gap-3'>
							<DropzoneZone>
								<DropzoneInput />
								<Empty className='p-4!'>
									<EmptyHeader>
										<EmptyMedia variant='icon'>
											<DropzoneUploadIcon />
										</EmptyMedia>
										<EmptyTitle>
											{t('dropzone.title', {
												defaultValue: 'Drop files here or click to upload',
											})}
										</EmptyTitle>
										<EmptyDescription>
											{t('dropzone.description', {
												defaultValue:
													'You can upload files up to 10MB in size. Supported formats: CSV, XLS, XLSX.',
											})}
										</EmptyDescription>
									</EmptyHeader>
								</Empty>
							</DropzoneZone>

							{field.value?.map((file: File) => (
								<Attachment className='w-full' key={file.name}>
									<AttachmentMedia>
										<FileIcon />
									</AttachmentMedia>
									<AttachmentContent>
										<AttachmentTitle>{file.name}</AttachmentTitle>
										<AttachmentDescription>
											{prettyBytes(file.size)} KB
										</AttachmentDescription>
									</AttachmentContent>
									<AttachmentActions>
										<AttachmentAction
											onClick={() =>
												field.onChange(
													field.value?.filter((f: File) => f !== file)
												)
											}
										>
											<XIcon />
										</AttachmentAction>
									</AttachmentActions>
								</Attachment>
							))}
						</div>
					</Dropzone>

					{!!description && <FormDescription>{description}</FormDescription>}
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
